import { useQueryClient } from '@tanstack/react-query';

/*  Call after successful flow submission
    (including login, logout).
    Lets app know to refresh user info and not attempt
    to re-use stale flows (ie initiate new ones when needed).
    This is important to keep the UI fresh.
*/
export function useInvalidateFlowsAndUser() {
  const queryClient = useQueryClient();

  return async function invalidateFlowsAndUser() {
    // We remove all queries for init/get kratos flow
    // from the cache entirely, rather than invalidate them,
    // as the old ones are now stale and attempting to get them
    // would only cause errors.  For the same reason, we
    // should **not** call a general invalidate until **after**
    // the flows are removed.
    //
    // We **also** need to nudge for a user info refresh, as
    // we may have just switched from 'authenticated' to 'guest'
    // or vice-versa (gained or lost a session cookie), so we
    // do need to invalidate that query.
    //
    // We can't just blanket `.invalidateQueries()` and `.clear()`
    // because doing so would cause errors/instabilities (and
    // reverse order is meaningless; the user query is gone before
    // it can be invalidated and dependent components don't react/
    // go stale). This is a situation where at least some of our
    // react query cache nudges **need** to be targeted.
    //
    // Note that a potential alternative approach (to pre-emptively
    // removing the stale flows) would be to get them again anyway,
    // but catch the stale flow errors and use that as the trigger
    // to init a fresh flow.  That might actually be 'cleaner' in
    // the sense that it respects the server as the 'single source
    // of truth' in terms of flow lifetime, but has the downside
    // of more error spam in the console.
    //
    // DETAIL:
    // This all goes against how caches, and React Query, should
    // work in 'nice' situations.
    //
    // In 'nice' situations there should be very little practical difference
    // between invalidating a query from the cache and removing a query from
    // the cache, and thus rare occasion to use clear/removeQueries, but...
    //
    // * invalidation preserves whatever query options & results were saved
    //   to that query key
    // * invalidation can potentially trigger an immediate refetch
    // * remove totally clears whatever query options & results were saved to
    //   that query key
    // * remove prevents potential immediate refetches
    //
    // Preventing immediate refetches of now-stale flows prevents the
    // exceptions that said refetch would cause; an invalidate might instead
    // provoke that exception immediately.
    //
    // The 'query options & results reset' functionality of reset is a bit more
    // subtle.  In a 'good' react query setup, it shouldn't actually make a
    // difference!  Ie, a good query is fully defined by query key such that
    // equal query keys imply functionally-equivalent query options and get
    // functionally-equivalent results.  Thus such a reset should not matter.
    //
    // Except, our queries are not 'good' queries!
    // Our queries break the rules:
    //
    // user info query keys should include (vary with) the session cookie, but
    // right now all user info queries for all possible session cookies
    // (or lack thereof) are tied to the same query key!
    //
    // the init flow queries aren't actually queries at all (they have a side
    // effect: creating a new flow).  They can't be keyed by a query key in a
    // 'react query happy' way at all!  (note: kratos made the same 'mistake'
    // by setting these as get method apis... they should probably be `POST`,
    // given the side effect).
    //
    // So long story short, the most subtle part of this is that we're using
    // `remove` to re-trigger the side-effect of the 'init flow' should-be-a-
    // mutation query.  Or, at least, queue such a re-triggering to happen.
    //
    // TODO(#5): clean this up
    queryClient.removeQueries({ queryKey: ['kratos', 'flow'] });
    await queryClient.invalidateQueries({ queryKey: ['kratos', 'whoami'] });
  };
}
